import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, MessageCircle, MessageSquare, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export const CREDIT_META = {
    whatsapp: { label: "WhatsApp", field: "whatsapp_credits", icon: MessageCircle, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    sms: { label: "SMS", field: "sms_credits", icon: MessageSquare, color: "text-sky-700 bg-sky-50 border-sky-200" },
    invoice: { label: "E-Fatura", field: "invoice_credits", icon: FileText, color: "text-amber-700 bg-amber-50 border-amber-200" },
};

export const LOW_CREDIT = 10;

export function lowCredits(data) {
    return Object.entries(CREDIT_META)
        .map(([k, m]) => ({ key: k, label: m.label, value: data?.[m.field] ?? 0 }))
        .filter((c) => c.value <= LOW_CREDIT);
}

const badgeColor = (v, base) =>
    v === 0 ? "text-white bg-rose-600 border-rose-600" : v <= LOW_CREDIT ? "text-rose-700 bg-rose-50 border-rose-300" : base;

export const LowCreditBanner = ({ data }) => {
    const low = lowCredits(data);
    if (!data || low.length === 0) return null;
    const empty = low.some((c) => c.value === 0);
    return (
        <div
            className={`mb-6 rounded-2xl border p-4 flex items-start gap-3 ${empty ? "bg-rose-50 border-rose-300 text-rose-800" : "bg-amber-50 border-amber-300 text-amber-900"}`}
            data-testid="low-credit-banner"
        >
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
                <div className="font-semibold">{empty ? "Kontür bitti!" : "Kontürünüz azalıyor"}</div>
                <div className="mt-0.5">
                    {low.map((c) => `${c.label}: ${c.value === 0 ? "bitti" : `${c.value} kaldı`}`).join(" • ")}
                    {" — "}gönderimlerin kesilmemesi için sistem yöneticinizden kontür yükletin.
                </div>
            </div>
        </div>
    );
};

export const LowCreditClinics = ({ clinics, onOpen }) => {
    const rows = clinics.map((c) => ({ c, low: lowCredits(c) })).filter((r) => r.low.length > 0);
    if (rows.length === 0) return null;
    return (
        <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50/60 p-5" data-testid="low-credit-clinics">
            <div className="flex items-center gap-2 font-semibold text-rose-800 mb-3">
                <AlertTriangle className="w-4 h-4" /> Kontürü azalan klinikler ({rows.length})
            </div>
            <div className="space-y-2">
                {rows.map(({ c, low }) => (
                    <div key={c.id} className="flex items-center justify-between gap-3 bg-white rounded-xl border border-rose-100 px-4 py-2" data-testid={`low-credit-clinic-${c.id}`}>
                        <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">{c.name}</div>
                            <div className="text-xs text-rose-700">
                                {low.map((l) => `${l.label}: ${l.value === 0 ? "bitti" : l.value}`).join(" • ")}
                            </div>
                        </div>
                        <Button size="sm" variant="outline" className="text-amber-700 border-amber-200 hover:bg-amber-50" onClick={() => onOpen(c.id)} data-testid={`low-credit-load-${c.id}`}>
                            Kontür Yükle
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const CreditBadges = ({ data, testPrefix }) => (
    <div className="flex flex-wrap gap-1.5">
        {Object.entries(CREDIT_META).map(([k, m]) => (
            <span
                key={k}
                className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border ${badgeColor(data?.[m.field] ?? 0, m.color)}`}
                data-testid={`${testPrefix}-${k}`}
                title={`${m.label} kontürü`}
            >
                <m.icon className="w-3 h-3" />
                {data?.[m.field] ?? 0}
            </span>
        ))}
    </div>
);

export const CreditLogList = ({ logs }) => (
    <div className="max-h-64 overflow-y-auto divide-y text-sm" data-testid="credit-log-list">
        {logs.length === 0 && <div className="py-6 text-center text-slate-500">Henüz hareket yok.</div>}
        {logs.map((l) => (
            <div key={l.id} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate">
                        {CREDIT_META[l.type]?.label} • {l.reason}
                    </div>
                    <div className="text-xs text-slate-500">
                        {l.created_at?.slice(0, 16).replace("T", " ")} • {l.by}
                    </div>
                </div>
                <div className="text-right flex-shrink-0">
                    <div className={`mono font-semibold ${l.amount > 0 ? "text-emerald-700" : "text-rose-600"}`}>
                        {l.amount > 0 ? "+" : ""}{l.amount}
                    </div>
                    <div className="text-[11px] text-slate-500">Bakiye: {l.balance_after}</div>
                </div>
            </div>
        ))}
    </div>
);

export default function CreditDialog({ clinic, open, onOpenChange, onSaved }) {
    const [type, setType] = useState("whatsapp");
    const [amount, setAmount] = useState(100);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [logs, setLogs] = useState([]);

    async function loadLogs() {
        const { data } = await api.get(`/admin/clinics/${clinic.id}/credit-logs`);
        setLogs(data);
    }

    useEffect(() => {
        if (open && clinic) loadLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, clinic]);

    async function submit(sign) {
        setSaving(true);
        try {
            await api.post(`/admin/clinics/${clinic.id}/credits`, {
                type,
                amount: sign * Math.abs(Number(amount)),
                note,
            });
            toast.success(`${CREDIT_META[type].label} kontürü ${sign > 0 ? "yüklendi" : "düşüldü"}`);
            setNote("");
            loadLogs();
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    if (!clinic) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="credit-dialog">
                <DialogHeader>
                    <DialogTitle>Kontür Yönetimi — {clinic.name}</DialogTitle>
                </DialogHeader>
                <CreditBadges data={clinic} testPrefix="credit-dialog-balance" />
                <div className="grid grid-cols-3 gap-2">
                    {Object.entries(CREDIT_META).map(([k, m]) => (
                        <Button
                            key={k}
                            type="button"
                            size="sm"
                            variant={type === k ? "default" : "outline"}
                            className={type === k ? "dk-btn-primary" : ""}
                            onClick={() => setType(k)}
                            data-testid={`credit-type-${k}`}
                        >
                            <m.icon className="w-3.5 h-3.5 mr-1" />
                            {m.label}
                        </Button>
                    ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label>Adet</Label>
                        <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="credit-amount-input" />
                    </div>
                    <div>
                        <Label>Not (opsiyonel)</Label>
                        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ör. Haziran paketi" data-testid="credit-note-input" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Button className="dk-btn-primary" disabled={saving || !Number(amount)} onClick={() => submit(1)} data-testid="credit-add-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `+ ${amount} Yükle`}
                    </Button>
                    <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" disabled={saving || !Number(amount)} onClick={() => submit(-1)} data-testid="credit-remove-btn">
                        − {amount} Düş
                    </Button>
                </div>
                <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Kullanım Geçmişi</div>
                    <CreditLogList logs={logs} />
                </div>
            </DialogContent>
        </Dialog>
    );
}
